import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useRef } from 'react';

import { getFormsFiltersSelector } from 'store/selectors/requestFormProcess';

import {
  getAllFormProcessEffect,
  getDraftFormsEffect,
  getFormMetaEffect,
  resetSmartFormDataEffect,
  setFormsTabEffect,
} from 'store/effects/formProcess';
import { getUserRolesMapSelector } from 'store/selectors/user';

import { getAggregatePageTypeSelector } from 'store/selectors/workshop';

import { LocationService } from 'services';
import { addProOrTransFilterParam } from 'utils/aggregatedPageTypeHelper';

import { requestGetProjectsEffect, requestGetTransactionsEffect } from 'store/effects';

import { updateFormsFiltersAction } from 'store/actions/formProcess';
import { filter } from 'jszip';

const DEFAULT_INTERVAL_TIME = 5000;

interface locationTypes {
  showDraftsModal?: boolean;
  showFormModal?: boolean;
  FormProcessId?: number;
}

const locationService = new LocationService();

export const useManageForms = () => {
  const { search, ...restFilters } = useSelector(getFormsFiltersSelector);
  const [loadingForms, setLoadingForms] = useState<boolean>(false);
  const [intervalTime, setIntervalTime] = useState(DEFAULT_INTERVAL_TIME);

  const [newFormModal, setNewFormModal] = useState(false);

  const [showDraftModal, setShowDraftModal] = useState(false);
  const [selectedFormProcess, setSelectedFormProcess] = useState<number>();
  const [selectedFormStatus, setSelectedFormStatus] = useState<string>();
  const [showSelection, setShowSelection] = useState<boolean>(false);
  const [selectedFormIds, setSelectedFormIds] = useState<number[]>([]);
  const [selectedFormNames, setSelectedFormNames] = useState<string[]>([]);
  const [showFormUpdateModal, setShowFormUpdateModal] = useState<boolean>(false);

  const filterRef = useRef<any>();
  const preventNextFetch = useRef<any>();

  const { isAgent } = useSelector(getUserRolesMapSelector);

  const dispatch = useDispatch();
  const location: any = useLocation();

  const formFilters = JSON.stringify(restFilters || {});

  const locationSrv = new LocationService();
  locationSrv.setLocation(location);
  const query = locationSrv.getQuery();

  const locationState = location.state as locationTypes;
  locationService.setLocation(location);

  const hasSignatureInLocation = location.search.includes('from=signature');
  const aggregatedPageType = useSelector(getAggregatePageTypeSelector);

  useEffect(() => {
    if (location?.state?.openNewFormModal) setNewFormModal(true);
  }, []);

  useEffect(() => {
    setSelectedFormIds([]);
    setSelectedFormNames([]);
  }, [showSelection]);

  useEffect(() => {
    locationState?.showDraftsModal && setShowDraftModal(true);

    // open form modal redirected from agent dashboard
    if (locationState?.showFormModal) {
      setSelectedFormProcess(locationState?.FormProcessId);
    }

    if (isAgent) {
      dispatch(getFormMetaEffect());
    }

    dispatch(requestGetTransactionsEffect({}));
    dispatch(requestGetProjectsEffect({ filters: { isProject: true } }));
    dispatch(resetSmartFormDataEffect());

    if ('statuses' in query) {
      preventNextFetch.current = true;
      let filters = JSON.parse(formFilters);
      filters = {
        ...filters,
        statuses: [query.statuses],
      };

      setLoadingForms(true);
      const modifiedFilter = { ...filters };
      dispatch(updateFormsFiltersAction(modifiedFilter));

      fetchAllForms({}, undefined, modifiedFilter);
    } else {
      fetchAllForms();
    }

    return () => {
      dispatch(setFormsTabEffect(undefined));
      dispatch(updateFormsFiltersAction({ search: undefined }));
    };
  }, []);

  useEffect(() => {
    if (preventNextFetch.current) {
      preventNextFetch.current = false;
      return;
    }

    filterRef.current && fetchAllForms();
  }, [aggregatedPageType, formFilters]);

  useEffect(() => {
    let intervalId;
    if (hasSignatureInLocation && intervalTime) {
      const fetchData = () => {
        fetchAllForms({ silent: true }, () => {
          setIntervalTime((prev) => prev + DEFAULT_INTERVAL_TIME);
        });
      };

      intervalId = setInterval(fetchData, intervalTime);
    }

    return () => {
      intervalId && clearInterval(intervalId);
    };
  }, [hasSignatureInLocation, aggregatedPageType, formFilters, intervalTime]);

  useEffect(() => {
    if (showDraftModal) {
      dispatch(getDraftFormsEffect());
    }
  }, [showDraftModal]);

  const fetchAllForms = (options?, cb?, appliedFilters?) => {
    let filters = appliedFilters || JSON.parse(formFilters);
    !options?.silent && setLoadingForms(true);

    dispatch(
      getAllFormProcessEffect(
        {
          filters: {
            ...addProOrTransFilterParam(aggregatedPageType, {
              ...(filters && filters),
            }),
          },
        },
        options && options,
        (error, response) => {
          if (!error) {
            setLoadingForms(false);
            cb?.();
            filterRef.current = true;
          }
        },
      ),
    );
  };

  const onCloseModal = () => {
    setShowDraftModal(false);
  };

  // Closing Detail Modal
  const onCloseFormDetailModal = () => {
    setSelectedFormProcess(undefined);
  };

  return {
    loadingForms,
    onCloseModal,
    onCloseFormDetailModal,
    setNewFormModal,
    selectedFormIds,
    selectedFormNames,
    setSelectedFormNames,
    setSelectedFormIds,
    showFormUpdateModal,
    setShowFormUpdateModal,
    fetchAllForms,
    setShowSelection,
    newFormModal,
    selectedFormProcess,
    setSelectedFormProcess,
    selectedFormStatus,
    setSelectedFormStatus,
    showDraftModal,
    showSelection,
    isFilterApplied: Object.keys(restFilters).length,
  };
};
